import React, { useEffect } from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useLang } from '~/hooks/useLang';
import { getObservations } from '~/features/Dispatch/services';
import { useHookstate } from '@hookstate/core';
import dayjs from 'dayjs';

interface RegulatoryObservationsProps {
  readonly id: number;
  readonly notes: string;
  readonly created_at: string;
  readonly user: {
    readonly id: number;
    readonly name: string;
    readonly representation_name: string;
  };
};

const tableStyles = {
  container: {
    width: '100%',
    border: '1px solid #000',
    borderCollapse: 'collapse' as const,
    fontSize: '12px',
    '@media print': {
      fontSize: '10px',
      width: '100%',
      tableLayout: 'fixed' as const,
    },
  },
  headerCell: {
    border: '1px solid #000',
    padding: '8px 4px',
    fontWeight: 'bold',
    textAlign: 'start' as const,
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    '@media print': {
      padding: '4px 2px',
      fontSize: '10px',
      backgroundColor: 'transparent',
    },
  },
  dataCell: {
    border: '1px solid #000',
    padding: '8px 4px',
    textAlign: 'left' as const,
    color: '#000000DE',
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    wordWrap: 'break-word' as const,
    '@media print': {
      padding: '3px 2px',
      fontSize: '0.875rem',
    },
  },
};

export default function RegulatoryObservations({ id }) {
  const { translate } = useLang();
  const regulatoryObservations = useHookstate<readonly RegulatoryObservationsProps[] | null>(null);

  useEffect(() => {
    getObservations(id).then((response) => {
      regulatoryObservations.set(response)
    })
  }, [id])

  if (!regulatoryObservations.get()?.length) {
    return (
      <Container fixed={false}>
        <Box sx={{ textAlign: 'center', marginTop: 2 }}>
          <Typography variant="body1">
            <b>
              {translate('NO INFORMATION REGISTERED')}
            </b>
          </Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container fixed={false}>
      <Box component="table" sx={tableStyles.container}>
        <Box component="thead">
          <Box component="tr">
            <Box
              component="th"
              sx={{ ...tableStyles.headerCell, width: '20%' }}
            >
              DATA E HORA
            </Box>
            <Box
              component="th"
              sx={{ ...tableStyles.headerCell, width: '20%' }}
            >
              USUÁRIO
            </Box>
            <Box
              component="th"
              sx={{ ...tableStyles.headerCell, width: '60%' }}
            >
              OBSERVAÇÃO
            </Box>
          </Box>
        </Box>
        <Box component="tbody">
          {regulatoryObservations.get()?.map(row => (
            <Box component="tr" key={row.id}>
              <Box component="td" sx={tableStyles.dataCell}>
                {row.created_at ? dayjs(row.created_at).format('DD/MM/YYYY HH:mm') : 'NI'}
              </Box>
              <Box component="td" sx={tableStyles.dataCell}>
                {row.user.representation_name}
              </Box>
              <Box component="td" sx={tableStyles.dataCell}>
                {row.notes}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Typography variant='caption' sx={{ mt: 1, display: 'block' }}>
        NI - Não Informado
      </Typography>
    </Container>
  );
}